import { ComponentProps, useEffect, useState } from 'react';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { isLoggedIn } from '../api/auth';
import { ChildProfile, getActiveProfile, getCachedActiveProfile } from '../api/profiles';
import { avatarSource } from '../utils/avatars';

type IoniconName = ComponentProps<typeof Ionicons>['name'];
type Navigation = NativeStackNavigationProp<RootStackParamList>;

type ActionHeaderProps = {
  onBack?: () => void;
  rightIcons?: IoniconName[];
  onRightPress?: Array<() => void>;
};

const logoSun = require('../../assets/aural-logo-sun.png');
const logoFulture = require('../../assets/fulture.png');
const logoVtv = require('../../assets/logo-vtv.png');
const logoHoiDongDoi = require('../../assets/logo-hoidongdoi.png');

export function BrandHeader() {
  const navigation = useNavigation<Navigation>();
  const [profile, setProfile] = useState<ChildProfile | null>(() => getCachedActiveProfile());
  const [loggedIn, setLoggedIn] = useState(() => isLoggedIn());

  useEffect(() => {
    let mounted = true;

    const loadProfile = () => {
      const hasSession = isLoggedIn();
      setLoggedIn(hasSession);

      if (!hasSession) {
        setProfile(null);
        return;
      }

      const cached = getCachedActiveProfile();
      if (cached) {
        setProfile(cached);
      }

      getActiveProfile()
        .then((item) => {
          if (mounted) setProfile(item);
        })
        .catch(() => {
          if (mounted) setProfile(null);
        });
    };

    loadProfile();
    const unsubscribe = navigation.addListener('focus', loadProfile);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [navigation]);

  return (
    <View style={styles.brandHeader}>
      <View style={styles.brandLayout}>
        <View style={styles.brandLeft}>
          <Image source={logoVtv} style={styles.partnerLogoVtv} resizeMode="contain" />
          <Image source={logoSun} style={styles.logoSun} resizeMode="contain" />
        </View>
        <View style={styles.brandRight}>
          <Image source={logoHoiDongDoi} style={styles.partnerLogoHoiDongDoi} resizeMode="contain" />
          <Pressable
            style={[styles.userButton, loggedIn && profile && styles.profileButton]}
            onPress={() => navigation.navigate(loggedIn && profile ? 'ProfileSelect' : 'Login')}
            hitSlop={10}
          >
            {loggedIn && profile ? (
              <>
                <Image source={avatarSource(profile.avatar_url)} style={styles.profileAvatar} resizeMode="contain" />
                <Text style={styles.profileName} numberOfLines={1}>{profile.nickname}</Text>
              </>
            ) : (
              <Ionicons name="person-circle-outline" size={28} color={colors.textPrimary} />
            )}
          </Pressable>
          <Image source={logoFulture} style={styles.logoFulture} resizeMode="contain" />
        </View>
      </View>
    </View>
  );
}

export function ActionHeader({ onBack, rightIcons = ['heart-outline', 'ellipsis-horizontal'], onRightPress = [] }: ActionHeaderProps) {
  const actions = rightIcons
    .map((icon, index) => ({ icon, onPress: onRightPress[index] }))
    .filter((action) => action.onPress);

  return (
    <View style={styles.actionHeader}>
      <Pressable style={styles.iconButton} onPress={onBack} hitSlop={12}>
        <Ionicons name="chevron-back" size={24} color={colors.textPrimary} />
      </Pressable>
      <View style={styles.rightActions}>
        {actions.map((action, index) => (
          <Pressable key={`${action.icon}-${index}`} style={styles.iconButton} onPress={action.onPress} hitSlop={12}>
            <Ionicons name={action.icon} size={24} color={colors.textPrimary} />
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  brandHeader: {
    minHeight: 172,
    marginBottom: 12,
  },
  brandLayout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
  brandLeft: {
    alignItems: 'flex-start',
    flex: 1,
  },
  partnerLogoVtv: {
    width: 96,
    height: 52,
  },
  partnerLogoHoiDongDoi: {
    width: 47,
    height: 52,
  },
  logoSun: {
    width: 150,
    height: 150,
    marginTop: -2,
  },
  logoFulture: {
    width: 120,
    height: 120,
  },
  brandRight: {
    alignItems: 'flex-end',
    paddingTop: 0,
    flex: 1,
  },
  userButton: {
    minWidth: 42,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.86)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 7,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    marginBottom: -14,
    zIndex: 2,
  },
  profileButton: {
    flexDirection: 'row',
    gap: 6,
    maxWidth: 132,
  },
  profileAvatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
  },
  profileName: {
    color: colors.textPrimary,
    fontSize: 12,
    fontWeight: '900',
    maxWidth: 82,
  },
  actionHeader: {
    height: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  rightActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  iconButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
